import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'Store/index';
import DataTable from 'Components/DataTable';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import { Container, Item } from 'Components/Grid';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import React from 'react';
import InputHandler from 'Components/InputHandler';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { actions } from 'Store/patient/actions';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { validateSSN } from 'neuro-utils';
import { fetchInformantBySsn, linkPatientToInformant } from 'Store/patient/apiFetchers';
import { dialogActions, dialogContent, dialogTitle } from '../../../../../config/theme/componentTheme';
import { reduxActions } from 'Store/patient';
import phone from 'phone';
import { validate } from '@stellarq/email';
import { fetchMyUserContactInfo, fetchMyUserDataBySsn } from 'Store/myapp/fetchers';
import { blurActiveElement } from 'Utility/domFocus';
import Icon from 'Components/_NewElements/Icon';
import { Icons } from 'Components/_NewElements/Icon/AvailableIcons';
import { PatientCountryCode } from 'neuro-constants';

const StyledMenuIcon = ({ ...other }) => (
  <IconButton
    disableRipple
    disableFocusRipple
    {...other}
    sx={{ padding: '0 1rem', color: '#6c96ae', cursor: 'pointer' }}
  >
    <MoreVertIcon />
  </IconButton>
);

const initialNewInformantData: {
  newSsnLocalization: PatientCountryCode;
  newSsn?: string;
  newLastname?: string;
  newFirstname?: string;
  newPhone?: string;
  newEmail?: string;
} = {
  newSsnLocalization: 'FIN',
  newSsn: undefined,
  newLastname: undefined,
  newFirstname: undefined,
  newPhone: undefined,
  newEmail: undefined,
};

const initialTempFieldData: {
  tempSsnLocalization: PatientCountryCode;
  tempSsn?: string;
  tempLastname?: string;
  tempFirstname?: string;
  tempPhone?: string;
  tempEmail?: string;
} = {
  tempSsnLocalization: 'FIN',
  tempSsn: undefined,
  tempLastname: undefined,
  tempFirstname: undefined,
  tempPhone: undefined,
  tempEmail: undefined,
};

const visibleRowFields: Array<keyof IInformant> = ['ssn', 'lastNames', 'firstNames', 'phoneNumber', 'email'];
const newFields: Array<keyof typeof initialNewInformantData> = [
  'newSsn',
  'newLastname',
  'newFirstname',
  'newPhone',
  'newEmail',
];

const getTempFieldName = (field: keyof IInformant): keyof typeof initialTempFieldData => {
  switch (field) {
    case 'ssn':
      return 'tempSsn';
    case 'lastNames':
      return 'tempLastname';
    case 'firstNames':
      return 'tempFirstname';
    case 'phoneNumber':
      return 'tempPhone';
    case 'email':
      return 'tempEmail';
    default:
      return 'tempFirstname';
  }
};

const Delegates = () => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const dispatch = useAppDispatch();

  const delegates = useAppSelector(state => state.patient.delegates) || [];
  const activeDelegates = delegates.filter(d => d.expired_at === null || d.expired_at > Date.now());

  const patientAllInformants = useAppSelector(state => state.patient.informants) || [];
  const patientInformants = patientAllInformants.filter(pi => !pi.removed);

  const allowedPatientCountries = useAppSelector(state => state.settings.orgSettings.allowedPatientCountries);
  const ssnCountries = allowedPatientCountries?.map(apc => apc.code) ?? ['FIN'];
  const defaultLocale =
    (allowedPatientCountries?.length ?? 0) > 1 ? allowedPatientCountries?.find(apc => apc.isDefault)?.code : null;

  const myAppDocs = useAppSelector(state => state.myapp.sortedAndMergedDocuments);

  const [addingNewInformant, setAddingNewInformant] = React.useState<boolean>(false);
  const [newInformantData, setNewInformantData] = React.useState(initialNewInformantData);

  const [editingInformantId, setEditingInformantId] = React.useState<undefined | string>(undefined);
  const [tempEditInformantData, setTempEditInformantData] = React.useState(initialTempFieldData);

  const [deleteId, setDeleteId] = React.useState<string | undefined>(undefined);
  const [informantMenuOpen, setInformantMenuOpen] = React.useState<
    { id: string; anchor: null | HTMLElement } | undefined
  >(undefined);

  const [existingInformantMenuOpen, setExistingInformantMenuOpen] = React.useState<boolean>(false);
  const [existingInformant, setExistingInformant] = React.useState<IInformant | null>(null);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [fieldValidity, setFieldValidity] = React.useState<Record<string, { valid: boolean; invalidText?: string }>>(
    {},
  );

  const [dataFound, setDataFound] = React.useState<boolean>(false);

  const handleMysqUserDataFetching = async (ssn: string, ssnLocalization: PatientCountryCode) => {
    setLoading(true);
    const userData = await fetchMyUserDataBySsn(ssn, ssnLocalization);
    if (!userData) {
      setLoading(false);
      return;
    }
    setDataFound(true);
    const contacts = await fetchMyUserContactInfo(userData.userId);
    const email = contacts?.find(c => c.contactType === 'email')?.contactValue?.[0];
    const phone = contacts?.find(c => c.contactType === 'sms')?.contactValue?.[0];
    const firstName = userData?.firstNames;
    const lastName = userData?.lastNames;
    setNewInformantData(prev => ({
      newSsnLocalization: ssnLocalization,
      newSsn: prev.newSsn,
      newLastname: lastName,
      newFirstname: firstName,
      newPhone: phone,
      newEmail: email,
    }));
    setLoading(false);
  };

  const handleNewInformantSave = async () => {
    if (!newInformantData.newSsn) return;
    const createdOrExists = await dispatch(
      actions.createPatientInformant({
        ssn: newInformantData.newSsn,
        ssnLocalization: newInformantData.newSsnLocalization,
        lastNames: newInformantData.newLastname || null,
        firstNames: newInformantData.newFirstname || null,
        phoneNumber: newInformantData.newPhone || null,
        email: newInformantData.newEmail || null,
        removed: null,
        mysqUserId: null,
        source: 'neuro-ui',
        kind: null,
        isActive: true,
      }),
    );
    if (createdOrExists?.includes('exists')) {
      const existingInformant = await fetchInformantBySsn(newInformantData.newSsn, newInformantData.newSsnLocalization);
      if (dataFound) {
        if (existingInformant) {
          await handleInformantLink({
            ...existingInformant,
            ssn: newInformantData.newSsn ?? existingInformant.ssn,
            lastNames: newInformantData.newLastname ?? null,
            firstNames: newInformantData.newFirstname ?? null,
            phoneNumber: newInformantData.newPhone ?? null,
            email: newInformantData.newEmail ?? null,
          });
          return;
        }
      }
      setExistingInformant(existingInformant);
      setExistingInformantMenuOpen(true);
    } else {
      setAddingNewInformant(false);
      setNewInformantData(initialNewInformantData);
      setFieldValidity({});
    }
  };

  const handlePatchInformant = async () => {
    if (!editingInformantId) return;
    const oldData = patientInformants.find(pi => pi.id === editingInformantId);
    if (!oldData) return;
    await dispatch(
      actions.updatePatientInformant({
        id: oldData.id,
        ssn:
          tempEditInformantData.tempSsn || oldData.ssnLocalization !== tempEditInformantData.tempSsnLocalization
            ? (tempEditInformantData.tempSsn ?? oldData.ssn)
            : undefined,
        ssnLocalization:
          tempEditInformantData.tempSsn || oldData.ssnLocalization !== tempEditInformantData.tempSsnLocalization
            ? tempEditInformantData.tempSsnLocalization
            : undefined,
        lastNames: tempEditInformantData.tempLastname || undefined,
        firstNames: tempEditInformantData.tempFirstname || undefined,
        phoneNumber: tempEditInformantData.tempPhone || undefined,
        email: tempEditInformantData.tempEmail || undefined,
      }),
    );
    setEditingInformantId(undefined);
    setTempEditInformantData(initialTempFieldData);
    setFieldValidity({});
  };

  const handleInformantUnlink = async (id: string) => {
    await dispatch(actions.unlinkPatFromInformant(id));
  };

  const handleInactivateInformant = async (id: string) => {
    await dispatch(actions.updatePatientInformant({ id, isActive: false }));
  };

  const handleReactivateInformant = async (id: string) => {
    await dispatch(actions.updatePatientInformant({ id, isActive: true }));
  };

  const handleInformantLink = async (existInformant?: IInformant) => {
    const informant = existInformant ?? existingInformant;
    if (!informant) return;
    await linkPatientToInformant(informant.id);
    dispatch(reduxActions.linkPatientToInformant({ ...informant, isActive: true }));
    setAddingNewInformant(false);
    setNewInformantData(initialNewInformantData);
    setExistingInformantMenuOpen(false);
    setFieldValidity({});
  };

  const handleInformantLinkAndUpdate = async () => {
    if (!existingInformant) return;
    await linkPatientToInformant(existingInformant.id);
    dispatch(reduxActions.linkPatientToInformant({ ...existingInformant, isActive: true }));
    await dispatch(
      actions.updatePatientInformant({
        id: existingInformant.id,
        ssn: newInformantData.newSsn || undefined,
        ssnLocalization: newInformantData.newSsn ? newInformantData.newSsnLocalization : undefined,
        lastNames: newInformantData.newLastname || undefined,
        firstNames: newInformantData.newFirstname || undefined,
        phoneNumber: newInformantData.newPhone || undefined,
        email: newInformantData.newEmail || undefined,
      }),
    );
    setAddingNewInformant(false);
    setNewInformantData(initialNewInformantData);
    setExistingInformantMenuOpen(false);
    setFieldValidity({});
  };

  const validateField = (
    field: string,
    value: string,
    ssnLocalization?: PatientCountryCode,
  ): { valid: boolean; invalidText?: string } => {
    let result: { valid: boolean; invalidText?: string } = { valid: true, invalidText: undefined };

    if (field === 'newSsn' || field === 'tempSsn') {
      const validSSN = !ssnLocalization ? false : validateSSN(ssnLocalization, { ssn: value }).isValid;
      result = { valid: validSSN, invalidText: fm('general.checkSsn') };
    } else if (field === 'newPhone' || field === 'tempPhone') {
      if (value.length === 0) {
        result = { valid: true, invalidText: undefined };
      } else {
        const parsedPhone = phone(value, { country: 'FIN' });
        result = { valid: parsedPhone.isValid, invalidText: fm('delegates.checkPhone') };
      }
    } else if (field === 'newEmail' || field === 'tempEmail') {
      if (value.length === 0) {
        result = { valid: true, invalidText: undefined };
      } else {
        result = { valid: validate(value), invalidText: fm('delegates.checkEmail') };
      }
    }

    setFieldValidity(prev => ({ ...prev, [field]: result }));
    return result;
  };

  const getFieldValidity = (field: string): { valid: boolean; invalidText?: string } => {
    return fieldValidity[field] || { valid: true };
  };

  const hasInvalidFields = Object.values(fieldValidity).some(v => !v.valid);

  const savingNewDisabled =
    (!(newInformantData.newSsn && (newInformantData.newEmail || newInformantData.newPhone)) || hasInvalidFields) &&
    !editingInformantId;
  const editingInformantOgData = patientInformants.find(pi => pi.id === editingInformantId);
  const savingEditDisabled =
    (!!(
      editingInformantId &&
      editingInformantOgData?.ssn === tempEditInformantData.tempSsn &&
      editingInformantOgData?.lastNames === tempEditInformantData?.tempLastname &&
      editingInformantOgData?.firstNames === tempEditInformantData?.tempFirstname &&
      editingInformantOgData?.email === tempEditInformantData?.tempEmail &&
      editingInformantOgData?.phoneNumber === tempEditInformantData?.tempPhone
    ) ||
      hasInvalidFields) &&
    !addingNewInformant;
  const saveDisabled = savingNewDisabled || savingEditDisabled;

  const showSsnLocalizationField = ssnCountries.length > 1 || ssnCountries.some(sc => sc !== 'FIN');

  return (
    <>
      <HistoryRowSubHeader header={fm('delegates.informants')} />
      <Container marginBottom={'2rem'}>
        <Item xs={12}>
          <DataTable
            headers={['ssn', 'lastNames', 'firstNames', 'phoneNumber', 'email', 'status']}
            headersFormatter={h =>
              h === 'phoneNumber' || h === 'email' || h === 'status' ? fm(`profile.${h}`) : fm(`general.${h}`)
            }
            data={{
              rowData: [
                ...patientInformants.map(pi => [
                  ...visibleRowFields.map(f => {
                    const isEditing = editingInformantId === pi.id;
                    const tempFieldName = getTempFieldName(f);
                    if (!isEditing && f === 'ssn') {
                      const showFlag =
                        defaultLocale !== null && pi.ssnLocalization !== defaultLocale && pi.ssnLocalization in Icons;
                      return (
                        <div key={pi.id} style={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                          {pi.ssn}
                          {showFlag ? (
                            <div style={{ marginLeft: '0.5rem' }}>
                              <Icon icon={pi.ssnLocalization} size={2} />
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      );
                    } else {
                      return (
                        <div key={pi.id + f} style={{ display: 'flex', alignItems: 'center' }}>
                          {f === 'ssn' && isEditing && showSsnLocalizationField ? (
                            <div style={{ marginRight: '1rem' }}>
                              <InputHandler
                                editing
                                type="Select"
                                name="tempSsnLocalization"
                                formData={{
                                  document: { tempSsnLocalization: tempEditInformantData?.tempSsnLocalization },
                                  onChange: (values: TOnChangeValues) => {
                                    const value = Object.values(values)?.[0];
                                    if (value === 'FIN' || value === 'SWE') {
                                      setTempEditInformantData(prev => ({ ...prev, tempSsnLocalization: value }));
                                    }
                                  },
                                }}
                                options={ssnCountries}
                                width={6}
                                minWidth={6}
                                fittedWidth={false}
                              />
                            </div>
                          ) : (
                            <></>
                          )}

                          <InputHandler
                            key={f}
                            type="TextField"
                            name={isEditing ? tempFieldName : f}
                            formData={{
                              document: isEditing
                                ? { [tempFieldName]: tempEditInformantData?.[tempFieldName] }
                                : { [f]: pi?.[f] },
                              onChange: (values: TOnChangeValues) => {
                                const value = Object.values(values)?.[0];
                                if (typeof value === 'string') {
                                  validateField(tempFieldName, value, tempEditInformantData.tempSsnLocalization);
                                  setTempEditInformantData(prev => ({ ...prev, [tempFieldName]: value }));
                                }
                              },
                            }}
                            editing={isEditing}
                            invalidValue={!getFieldValidity(tempFieldName).valid}
                            invalidText={getFieldValidity(tempFieldName).invalidText}
                          />
                        </div>
                      );
                    }
                  }),
                  <div key="status" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>{!pi.isActive ? fm('delegates.inactivated') : fm('delegates.active')}</div>
                    <StyledMenuIcon
                      onClick={(e: React.MouseEvent<HTMLElement>) =>
                        setInformantMenuOpen({ id: pi.id, anchor: e.currentTarget })
                      }
                    />
                  </div>,
                ]),
                addingNewInformant
                  ? [
                      ...newFields.map(field => {
                        return (
                          <div
                            key={field}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {field === 'newSsn' && showSsnLocalizationField ? (
                              <div style={{ marginRight: '1rem' }}>
                                <InputHandler
                                  editing
                                  type="Select"
                                  name="newSsnLocalization"
                                  formData={{
                                    document: { newSsnLocalization: newInformantData?.newSsnLocalization },
                                    onChange: (values: TOnChangeValues) => {
                                      const value = Object.values(values)?.[0];
                                      if (value === 'FIN' || value === 'SWE') {
                                        setNewInformantData(prev => ({ ...prev, newSsnLocalization: value }));
                                      }
                                    },
                                  }}
                                  options={ssnCountries}
                                  width={6}
                                  minWidth={6}
                                  fittedWidth={false}
                                />
                              </div>
                            ) : (
                              <></>
                            )}
                            <InputHandler
                              key={field}
                              type="TextField"
                              name={field}
                              formData={{
                                document: { [field]: newInformantData?.[field] },
                                onChange: async (values: TOnChangeValues) => {
                                  const value = Object.values(values)?.[0];
                                  if (typeof value === 'string') {
                                    const isValidField = validateField(
                                      field,
                                      value,
                                      newInformantData.newSsnLocalization,
                                    );
                                    setNewInformantData(prev => ({ ...prev, [field]: value }));
                                    if (field === 'newSsn') {
                                      if (isValidField.valid && value !== newInformantData.newSsn) {
                                        await handleMysqUserDataFetching(value, newInformantData.newSsnLocalization);
                                      } else if (!isValidField.valid && dataFound) {
                                        setDataFound(false);
                                        setNewInformantData({ ...initialNewInformantData, newSsn: value });
                                      }
                                    }
                                  }
                                },
                              }}
                              placeholder={fm(`profile.${field}`)}
                              disabled={
                                !!(
                                  field !== 'newSsn' &&
                                  (dataFound ||
                                    !newInformantData.newSsn ||
                                    (newInformantData.newSsn && !getFieldValidity('newSsn').valid))
                                )
                              }
                              editing={true}
                              invalidValue={!getFieldValidity(field).valid}
                              invalidText={getFieldValidity(field).invalidText}
                            />
                          </div>
                        );
                      }),
                      <></>,
                    ]
                  : [],
              ],
              rowStyle: patientInformants.map(pi => {
                if (!pi.isActive) {
                  return {
                    cellStyle: [...visibleRowFields, 'status'].map(f => ({ fontStyle: 'italic' })),
                  };
                } else return {};
              }),
            }}
          />
        </Item>
      </Container>
      <Container>
        {!addingNewInformant && !editingInformantId && (
          <Item xs={1.5}>
            <ActionButtonRounded
              text={'general.new'}
              onClick={() => setAddingNewInformant(true)}
              width={10}
              height={3}
              fontSize={16}
              uppercase={false}
            />
          </Item>
        )}
        {(addingNewInformant || editingInformantId) && (
          <>
            <Item xs={1.5}>
              <ActionButtonRounded
                text={'general.cancel'}
                onClick={() => {
                  setAddingNewInformant(false);
                  setNewInformantData(initialNewInformantData);
                  setEditingInformantId(undefined);
                  setTempEditInformantData(initialTempFieldData);
                  setFieldValidity({});
                  setDataFound(false);
                }}
                width={10}
                height={3}
                fontSize={16}
                uppercase={false}
                loading={loading}
              />
            </Item>
            <Item>
              <ActionButtonRounded
                text={'general.save'}
                onClick={async () => {
                  setLoading(true);
                  if (editingInformantId) {
                    await handlePatchInformant();
                  } else {
                    await handleNewInformantSave();
                  }
                  setLoading(false);
                }}
                width={10}
                height={3}
                fontSize={16}
                uppercase={false}
                filled
                loading={loading}
                disabled={saveDisabled}
              />
            </Item>
          </>
        )}
      </Container>
      {activeDelegates.length > 0 ? (
        <>
          <HistoryRowSubHeader header={fm('delegates.delegates')} />
          <Container>
            <Item>
              <div style={{ marginBottom: '0.5rem' }}>{fm('delegates.activeDelegates')}</div>
              <DataTable
                headers={['lastNames', 'firstNames']}
                headersFormatter={h => fm(`general.${h}`)}
                data={{
                  rowData: activeDelegates.map(ad => [ad.last_names, ad.first_names]),
                }}
                headerStyles={{ minWidth: 200 }}
              />
            </Item>
          </Container>
        </>
      ) : null}
      <Menu
        id="informantMenu"
        open={!!informantMenuOpen}
        onClose={() => {
          blurActiveElement();
          setInformantMenuOpen(undefined);
        }}
        anchorEl={informantMenuOpen?.anchor}
      >
        <MenuItem
          onClick={() => {
            if (informantMenuOpen?.id) {
              setEditingInformantId(informantMenuOpen.id);
              const informant = patientInformants.find(pi => pi.id === informantMenuOpen.id);
              setTempEditInformantData({
                tempSsnLocalization: informant?.ssnLocalization ?? 'FIN',
                tempSsn: informant?.ssn ?? '',
                tempLastname: informant?.lastNames ?? '',
                tempFirstname: informant?.firstNames ?? '',
                tempEmail: informant?.email ?? '',
                tempPhone: informant?.phoneNumber ?? '',
              });
              blurActiveElement();
              setInformantMenuOpen(undefined);
            }
          }}
          disabled={
            !!(
              loading ||
              !informantMenuOpen?.id ||
              patientInformants.find(pi => pi.id === informantMenuOpen.id)?.mysqUserId
            )
          }
        >
          {fm('general.edit')}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setLoading(true);
            const menuInformant = patientInformants.find(inf => inf.id === informantMenuOpen?.id);
            if (!menuInformant?.id) {
              setLoading(false);
              return;
            }
            if (!menuInformant.isActive) {
              await handleReactivateInformant(menuInformant.id);
            } else {
              await handleInactivateInformant(menuInformant.id);
            }
            blurActiveElement();
            setInformantMenuOpen(undefined);
            setLoading(false);
          }}
          disabled={loading || !informantMenuOpen?.id}
        >
          {informantMenuOpen && !patientInformants.find(inf => inf.id === informantMenuOpen.id)?.isActive
            ? fm('delegates.activate')
            : fm('delegates.inactivate')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteId(informantMenuOpen?.id);
            blurActiveElement();
            setInformantMenuOpen(undefined);
          }}
          disabled={
            loading ||
            !informantMenuOpen?.id ||
            myAppDocs.some(myDoc => {
              const infMysqId = patientInformants.find(inf => inf.id === informantMenuOpen.id)?.mysqUserId;
              if (!infMysqId) return false;
              return myDoc._creator === infMysqId;
            })
          }
        >
          {fm('general.delete')}
        </MenuItem>
      </Menu>
      <Dialog open={existingInformantMenuOpen} maxWidth={'md'}>
        <DialogTitle style={dialogTitle}>{fm('delegates.informantAlreadyExists')}</DialogTitle>
        <DialogContent style={dialogContent}>
          <DataTable
            headers={['', fm('delegates.foundInfo'), fm('delegates.enteredInfo')]}
            data={{
              rowData: [
                [fm('general.ssn'), existingInformant?.ssn || '-', newInformantData?.newSsn || '-'],
                [fm('general.lastNames'), existingInformant?.lastNames || '-', newInformantData?.newLastname || '-'],
                [fm('general.firstNames'), existingInformant?.firstNames || '-', newInformantData?.newFirstname || '-'],
                [fm('profile.phoneNumber'), existingInformant?.phoneNumber || '-', newInformantData?.newPhone || '-'],
                [fm('profile.email'), existingInformant?.email || '-', newInformantData?.newEmail || '-'],
              ],
            }}
          />
        </DialogContent>
        <DialogActions style={dialogActions}>
          <ActionButtonRounded
            text="general.cancel"
            onClick={() => {
              setExistingInformantMenuOpen(false);
              setAddingNewInformant(false);
              setNewInformantData(initialNewInformantData);
              setFieldValidity({});
            }}
            width={10}
            height={3}
            fontSize={16}
          />
          <ActionButtonRounded
            text="delegates.saveFound"
            onClick={async () => {
              setLoading(true);
              if (!existingInformant) return;
              await handleInformantLink();
              setLoading(false);
            }}
            width={16}
            height={3}
            fontSize={16}
            filled
            loading={loading}
          />
          <ActionButtonRounded
            text="delegates.saveAndOverwrite"
            onClick={async () => {
              setLoading(true);
              if (!existingInformant) return;
              await handleInformantLinkAndUpdate();
              setLoading(false);
            }}
            width={16}
            height={3}
            fontSize={16}
            filled
            loading={loading}
            disabled={!!existingInformant?.mysqUserId}
          />
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={!!deleteId}
        text={fm('general.reallyWantToDelete')}
        cancel={{ callback: () => setDeleteId(undefined) }}
        confirm={{
          callback: async () => {
            setLoading(true);
            const deleteInformant = patientInformants.find(pi => pi.id === deleteId);
            if (deleteInformant) {
              await handleInformantUnlink(deleteInformant.id);
            }
            setDeleteId(undefined);
            setLoading(false);
          },
        }}
      />
    </>
  );
};

export default Delegates;
